<template>
    <div id="etm-editor" class="wp-core-ui">

        <div id="etm-controls">

            <div id="etm-close-save">
                <a id="etm-controls-close" :href="closeURL" :title="editorStrings.close"></a>
                <button type="button" class="etm-help-toggle dashicons dashicons-editor-help" :class="{'etm-help-toggle-open' : helpPanelOpen, 'etm-help-toggle-never-opened' : !userMeta.helpPanelOpened }" @click="helpPanelOpen = !helpPanelOpen" aria-expanded="true" :title="editorStrings.quick_intro_title_attribute"></button>
                <div id="etm-save-and-loader-spinner">
                        <span class="etm-ajax-loader" v-show="loadingStrings > 0" id="etm-string-saved-ajax-loader">
                            <div class="etm-spinner"></div>
                        </span>
                    <save-translations
                            :selectedIndexesArray="selectedIndexesArray"
                            :dictionary="dictionary"
                            :settings="settings"
                            :nonces="nonces"
                            :ajax_url="ajaxUrl"
                            :currentLanguage="currentLanguage"
                            :onScreenLanguage="onScreenLanguage"
                            :iframe="iframe"
                            :currentURL="currentURL"
                            :mergingString="mergingString"
                            :mergeData="mergeData"
                            @translations-saved="showChangesUnsavedMessage = false"
                            :editorStrings="editorStrings"
                            :stringTypes="stringTypes"
                            :userMeta="userMeta"
                    >
                    </save-translations>
                </div>
            </div>

            <help-panel :helpPanelContent="helpPanelContent" :editorStrings="editorStrings" :helpPanelOpen="helpPanelOpen"></help-panel>

            <license-notice v-show="licenseNoticeContent" :licenseNoticeContent="licenseNoticeContent"></license-notice>

            <div class="etm-controls-container" :class="{'etm-show-editors-navigation' : editorsNavigation.show, 'help-panel-open':helpPanelOpen, 'etm-license-notice-shown':(licenseNoticeContent) }">

                <div class="etm-controls-section" id="etm-controls-section-first">

                    <div class="etm-controls-section-content">
                        <div id="etm-language-switch" :class="{'etm-highlight-for-panel': highlightLanguageSwitcher}">
                            <select id="etm-language-select" name="lang" v-model="currentLanguage" v-select2>
                                <option v-for="(lang, langIndex) in languageNames" :value="langIndex">{{lang}}</option>
                            </select>
                        </div>

                        <div id="etm-string-list">
                            <div :class="{'etm-highlight-for-panel': highlightStringList}">
                                <select id="etm-string-categories" v-model="selectedString" v-select2>
                                    <optgroup v-for="(group) in stringGroups" :label="group">
                                        <option v-for="(string, index) in dictionary" :value="index" v-if="showString( string, group )" :title="string.description" :data-database-id="string.dbID" :data-group="string.group">{{ processOptionName( string.original, group ) }}</option>
                                    </optgroup>
                                </select>
                            </div>
                        </div>

                        <div id="etm-next-previous">
                            <div class="etm-button-container">
                                <button type="button" id="etm-previous" class="etm-next-previous-buttons button" v-on:click="previousString()"><span>&laquo;</span> {{ editorStrings.previous }}</button>
                                <tooltip :text="editorStrings.previous_title_attr" class="etm-previous-tooltip" :editorStrings="editorStrings" :userMetaHandle="'dismissTooltipPrevious'" :userMeta="userMeta"></tooltip>
                            </div>
                            <div class="etm-button-container">
                                <button type="button" id="etm-next" class="etm-next-previous-buttons button" v-on:click="nextString()">{{ editorStrings.next }} <span>&raquo;</span></button>
                                <tooltip :text="editorStrings.next_title_attr" class="etm-next-tooltip" :editorStrings="editorStrings" :userMetaHandle="'dismissTooltipNext'" :userMeta="userMeta"></tooltip>
                            </div>
                        </div>

                        <div id="etm-view-as">
                            <div id="etm-view-as-description">{{ editorStrings.view_as }}</div>
                            <select id="etm-view-as-select" v-model="viewAs" v-select2>
                                <option v-for="(role, roleIndex) in roles" :value="role" :disabled="!role" :title="!role ? editorStrings.view_as_pro : ''">{{roleIndex}}</option>
                            </select>
                        </div>
                    </div>

                </div>

                <div class="etm-controls-section" v-show="selectedString !== null">
                    <language-boxes
                            :selectedIndexesArray="selectedIndexesArray"
                            :dictionary="dictionary"
                            :currentLanguage="currentLanguage"
                            :onScreenLanguage="onScreenLanguage"
                            :languageNames="languageNames"
                            :settings="settings"
                            :showChangesUnsavedMessage="showChangesUnsavedMessage"
                            @discarded-changes="hasUnsavedChanges()"
                            :editorStrings="editorStrings"
                            :flagsPath="flagsPath"
                            :flags-file-name="flagsFileName"
                            :iframe="iframe"
                            :nonces="nonces"
                            :ajax_url="ajaxUrl"
                            :userMeta="userMeta"
                    >
                    </language-boxes>
                </div>

                <extra-content :languageNames="languageNames" :editorStrings="editorStrings" :paidVersion="paidVersion"></extra-content>

                <div class="etm-controls-section" v-show="translationNotLoadedYet">
                    <div id="etm-translation-not-ready-section" class="etm-controls-section-content">
                        <p v-html="editorStrings.translation_not_loaded_yet"></p>
                    </div>
                </div>
            </div>

            <editors-navigation :editorsNavigation="editorsNavigation" :selectedTab="'visualeditor'"></editors-navigation>

            <div id="etm_select2_overlay"></div>

            <hover-actions
                ref="hoverActions"
                :dictionary="dictionary"
                :settings="settings"
                :iframe="iframe"
                :dataAttributes="dataAttributes"
                :mergeRules="mergeRules"
                :nonces="nonces"
                :ajax_url="ajaxUrl"
                :mergeData="mergeData"
                :editorStrings="editorStrings"
                :currentLanguage="currentLanguage"
            >
            </hover-actions>
        </div>

        <div id="etm-preview">
            <iframe id="etm-preview-iframe" :src="urlToLoad" v-on:load="iFrameLoaded"></iframe>

            <div id="etm-preview-loader" class="etm-loading-screen">
                <svg class="etm-loader" width="65px" height="65px" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
                    <circle class="etm-circle" fill="none" stroke-width="6" stroke-linecap="round" cx="33" cy="33" r="30"></circle>
                </svg>
            </div>
        </div>
    </div>
</template>

<script>
    import 'select2/dist/js/select2.min.js'
    import utils             from './utils'
    import axios             from 'axios'
    import languageBoxes     from './components/language-boxes.vue'
    import saveTranslations  from './components/save-translations.vue'
    import hoverActions      from './components/hover-actions.vue'
    import extraContent      from './components/extra-content.vue'
    import editorsNavigation from './components/editors-navigation.vue'
    import he                from 'he'
    import Tooltip           from "./components/tooltip"
    import HelpPanel         from "./components/help-panel"
    import LicenseNotice     from "./components/license-notice"

    export default {
        components:{
            HelpPanel,
            Tooltip,
            languageBoxes,
            saveTranslations,
            hoverActions,
            extraContent,
            editorsNavigation,
            LicenseNotice
        },
        data(){
            return {
                //props
                settings                  : etm_editor_data.etm_settings,
                languageNames             : etm_editor_data.language_names,
                orderedSecondaryLanguages : etm_editor_data.ordered_secondary_languages,
                roles                     : etm_editor_data.view_as_roles,
                nonces                    : etm_editor_data.editor_nonces,
                stringGroupOrder          : etm_editor_data.string_group_order,
                selectors                 : etm_editor_data.string_selectors,
                stringTypes               : etm_editor_data.string_types,
                dataAttributes            : etm_editor_data.data_attributes,
                mergeRules                : etm_editor_data.merge_rules,
                editorsNavigation         : etm_editor_data.editors_navigation,
                editorStrings             : etm_editor_data.etm_localized_strings,
                flagsPath                 : etm_editor_data.flags_path,
                flagsFileName             : etm_editor_data.flags_file_name,
                helpPanelContent          : etm_editor_data.help_panel_content,
                licenseNoticeContent      : etm_editor_data.license_notice_content,
                //data
                currentLanguage           : etm_editor_data.current_language,
                onScreenLanguage          : etm_editor_data.on_screen_language,
                currentURL                : etm_editor_data.url_to_load,
                urlToLoad                 : etm_editor_data.url_to_load,
                ajaxUrl                   : etm_editor_data.ajax_url,
                paidVersion               : etm_editor_data.paid_version,
                userMeta                  : etm_editor_data.user_meta,
                upgradedGettext           : etm_editor_data.upgraded_gettext,
                iframe                    : '',
                dictionary                : [],
                selectedString            : null,
                selectedIndexesArray      : [],
                detectedSelectorAndId     : [],
                stringGroups              : [],
                mergingString             : false,
                mergeData                 : [],
                showChangesUnsavedMessage : false,
                viewAs                    : '',
                loadingStrings           : 0,
                translationNotLoadedYet   : false,
                helpPanelOpen             : false,
                highlightLanguageSwitcher : false,
                highlightStringList       : false,
                gettextOriginalIds        : [],
                gettextNodeData           : [],
                gettextRequestsLeft       : 0,
                triggerAnotherScan        : false
            }
        },
        created(){
            this.settings['default-language-name'] = this.languageNames[ this.settings['default-language'] ]

            //set default value for the View As select
            let params = utils.getUrlParameters( this.currentURL )

            if( Object.keys(params).length > 1 && params['etm-view-as'] )
                this.viewAs = params['etm-view-as']
            else
                this.viewAs = 'current_user'
        },
        mounted(){
            this.addKeyboardShortcutsListener()
            this.addHelpPanelListeners()
            let self = this
            // initialize select2
            jQuery( '#etm-language-select, #etm-view-as-select' ).select2( { width : '100%' })

            //init strings dropdown
            this.stringsDropdownLoading()

            // show overlay when select is opened
            jQuery( '#etm-language-select, #etm-string-categories' ).on( 'select2:open', function() {
                jQuery( '#etm_select2_overlay' ).fadeIn( '100' )
            }).on( 'select2:close', function() {
                jQuery( '#etm_select2_overlay' ).hide()
            }).on( 'select2:opening', function(e) {
                /* when we have unsaved changes prevent the strings dropdown from opening so we do not have a disconnect between the textareas and the dropdown */
                if (self.hasUnsavedChanges()) {
                    e.preventDefault()
                }
            })

            // resize sidebar and consequently the iframe
            let previewContainer = jQuery( '#etm-preview' );
            let total_width = jQuery(window).width();
            jQuery( '#etm-controls' ).resizable({
                start: function( ) { previewContainer.toggle(); },
                stop: function( ) { previewContainer.toggle(); },
                handles: 'e',
                minWidth: 327,
                maxWidth: total_width - 20
            }).bind( "resize", this.resizeIframe );

            // resize iframe when resizing window
            jQuery( window ).resize(function () {
                self.resizeIframe();
            });

            document.addEventListener( 'etm_trigger_get_missing_gettext', this.getMissingGettextTranslations )
        },
        watch: {
            currentLanguage: function( currentLanguage ) {
                let self = this
                //grab the correct URL from the iFrame
                let newURL = this.iframe.querySelector( 'link[hreflang="' + currentLanguage.replace( '_', '-' ) +'"]' ).getAttribute('href')

                this.currentURL           = newURL
                this.iframe.location      = newURL

                //reset vue props
                this.selectedString       = ''
                this.selectedIndexesArray = []

                //set strings dropdown to loading state
                jQuery('#etm-string-categories').val('').trigger('change')
                this.stringsDropdownLoading()

                this.onScreenLanguage = currentLanguage
                if( this.settings['default-language'] == this.currentLanguage && this.settings['translation-languages'].length > 1 ){
                    this.settings['translation-languages'].some(function(language){
                        if ( language != self.settings['default-language'] ){
                            // return the first language not default
                            self.onScreenLanguage = language
                            return true
                        }
                    })
                }
            },
            currentURL: function ( newUrl, oldUrl ) {
                window.history.replaceState( null, null, this.parentURL( newUrl ) )
            },
            viewAs: function( role ) {
                if( !this.currentURL || !this.iframe )
                    return

                let url = this.cleanURL( this.currentURL )

                url = utils.updateUrlParameter( url, 'etm-edit-translation', 'preview' )

                if( role == 'current_user' ) {
                    this.iframe.location = url
                    return
                }

                //if nonce not available, an update to the Browse as Other Roles add-on is required
                if( !this.nonces[role] ) {
                    alert( this.editorStrings.bor_update_notice )
                    return
                }

                url = utils.updateUrlParameter( url, 'etm-view-as', role )
                url = utils.updateUrlParameter( url, 'etm-view-as-nonce', this.nonces[role] )

                this.iframe.location = url
            },
            selectedString: function ( selectedStringArrayIndex, oldString ){

                if( this.hasUnsavedChanges() || ( !selectedStringArrayIndex && selectedStringArrayIndex !== 0 ) )
                    return

                jQuery( '#etm-string-categories' ).val( selectedStringArrayIndex !== null ? selectedStringArrayIndex : '' ).trigger( 'change' )

                let selectedString       = this.dictionary[selectedStringArrayIndex]

                if( !selectedString )
                    return

                let currentNodes          = this.iframe.querySelectorAll( "[" + selectedString.selector + "='" + selectedString.dbID + "']")
                let selectedIndexesArray = []
                let self = this

                //when merging we do not have a valid current node, so we just add the fake id
                if( currentNodes.length > 0 ) {
                    let selectors = self.getAllSelectors()
                    let nodes = []

                    currentNodes.forEach( function ( currentNode ) {
                        nodes.push( currentNode )

                        if ( currentNode.tagName != "A" ){
                            // include the anchor's translatable attributes
                            let anchorParent  = currentNode.closest('a')
                            if(  anchorParent != null ) {
                                nodes.push(anchorParent)
                            }
                        }

                        if ( currentNode.tagName == "A" && currentNode.children.length > 0 ){
                            // include all the translatable attributes inside the anchor0
                            let childrenArray = [ ...currentNode.children ];
                            childrenArray.forEach( function ( child ) {
                                nodes.push(child)
                            })

                        }

                        nodes.forEach( function( node ) {
                            selectors.forEach(function (selector) {
                                let stringId = node.getAttribute(selector)
                                if (stringId) {
                                    let found = false
                                    let i
                                    for( i = 0; i < selectedIndexesArray.length; i++ ){
                                        if ( typeof self.dictionary[selectedIndexesArray[i]] !== 'undefined' && self.dictionary[selectedIndexesArray[i]].dbID !== 'undefined' && self.dictionary[selectedIndexesArray[i]].dbID === stringId ){
                                            found = true
                                            break;
                                        }
                                    }
                                    if ( ! found ) {
                                        selectedIndexesArray.push(self.getStringIndex(selector, stringId))
                                    }
                                }
                            })
                        })
                    })
                } else
                    selectedIndexesArray.push( selectedStringArrayIndex )

                if ( selectedString.originalPlural ){
                    this.dictionary.forEach( function ( string, index ) {
                        if ( string.originalId === selectedString.originalId && string.dbID !== selectedString.dbID ){
                            selectedIndexesArray.push( index )
                        }
                    } )
                    selectedIndexesArray.sort((a,b) => (self.dictionary[a].pluralForm > self.dictionary[b].pluralForm) ? 1 : ((self.dictionary[b].pluralForm > self.dictionary[a].pluralForm) ? -1 : 0))
                }


                this.selectedIndexesArray = selectedIndexesArray
            },
            helpPanelOpen : function(){
                if ( this.userMeta.helpPanelOpened !== true ){
                    document.dispatchEvent( new CustomEvent( 'etm_update_user_meta', {
                        'detail' : {
                            'userMetaKey' : 'helpPanelOpened',
                            'userMetaValue' : true,
                        }
                    } ) )
                }
            },
            gettextRequestsLeft : function( newValue, oldValue ){
                if ( oldValue > 0 && newValue === 0 ){
                    this.getGettextStringsDictionaries()
                }
            },
            loadingStrings : function( newValue, oldValue ){
                if ( oldValue > 0 && newValue === 0 && this.triggerAnotherScan ){
                    this.triggerAnotherScan = false
                    this.scanIframeForStrings()
                }
            }
        },
        computed: {
            closeURL: function() {
                return this.cleanURL( this.currentURL )
            }
        },
        methods: {
            iFrameLoaded(){
                let self = this
                let iframeElement = document.querySelector('#etm-preview-iframe')

                this.iframe = iframeElement.contentDocument || iframeElement.contentWindow.document

                //sync iFrame URL with parent
                if ( this.currentURL != this.iframe.URL )
                    this.currentURL = this.iframe.URL

                //hide iFrame loader
                this.iframeLoader( 'hide' )

                self.detectedSelectorAndId = []
                self.dictionary            = []
                this.scanIframeForStrings()

                window.addEventListener( 'etm_iframe_page_updated', this.iframePageUpdated )

                //event that is fired when the iFrame is navigated
                iframeElement.contentWindow.onbeforeunload = function() {
                    self.iframeLoader( 'show' )

                    self.selectedString = null
                    self.selectedIndexesArray = []
                    self.translationNotLoadedYet = false

                    self.stringsDropdownLoading()
                }

            },
            iframePageUpdated(){
                if ( this.loadingStrings > 0 ){
                    this.triggerAnotherScan = true
                }else{
                    this.scanIframeForStrings()
                }
            },
            scanIframeForStrings(){
                this.scanForSelector( 'data-etm-translate-id', 'regular', this.onScreenLanguage )
                if( this.upgradedGettext ){
                    this.scanForSelector( 'data-etmgettextoriginal', 'gettext', this.currentLanguage )
                }
                this.scanForSelector( 'data-etm-post-slug', 'postslug', this.currentLanguage )
            },
            scanForSelector( baseSelector, typeSlug, languageOfIds ){
                this.loadingStrings++
                let self           = this
                let selectors      = this.prepareSelectorStrings( baseSelector )
                let nodes          = [...this.iframe.querySelectorAll( '[' + selectors.join('],[') + ']' )]
                let stringIdsArray = [], nodeData = [], nodeEntries = []

                nodes.forEach( function ( node ){
                    nodeEntries = self.getNodeInfo( node, baseSelector )

                    nodeEntries.forEach( function( entry ) {
                        // this check ensures that we don't create duplicates when rescanning after ajax complete
                        if ( !self.alreadyDetected( entry.selector, entry.dbID ) ) {
                            stringIdsArray.push(entry.dbID)
                            nodeData.push(entry)
                        }
                    })

                    self.setupEventListener( node )
                })

                //unique ids only
                stringIdsArray = [...new Set(stringIdsArray)]
                if ( stringIdsArray.length > 0 ) {
                    let data = new FormData()
                    data.append('action'       , 'etm_get_translations_' + typeSlug)
                    data.append('all_languages', 'true')
                    data.append('security'     , this.nonces['gettranslationsnonce' + typeSlug])
                    data.append('language'     , languageOfIds)
                    data.append('string_ids'   , JSON.stringify(stringIdsArray))

                    axios.post(this.ajaxUrl, data)
                        .then(function (response) {
                            if ( typeSlug === 'gettext' ){
                                if ( response.data.originalIds ){
                                    self.gettextOriginalIds = response.data.originalIds
                                    self.gettextNodeData = nodeData
                                    document.dispatchEvent( new Event( 'etm_trigger_get_missing_gettext' ) )
                                }else{
                                    self.loadingStrings--
                                }
                            }else {
                                self.loadingStrings--
                                self.addToDictionary( response.data, nodeData )
                            }
                        })
                        .catch(function (error) {
                            console.log(error);
                        });
                }else{
                    self.loadingStrings--
                }

            },
            getMissingGettextTranslations(){

                let self = this
                self.settings[ 'translation-languages' ].forEach( function ( languageCode ) {
                    self.gettextRequestsLeft++
                    let data = new FormData()
                    data.append( 'action', 'etm_string_translation_get_missing_gettext_strings' )
                    data.append( 'original_ids', JSON.stringify( self.gettextOriginalIds ) )
                    data.append( 'etm_ajax_language', languageCode )
                    data.append( 'security', self.nonces['get_missing_strings'] )
                    axios.post( self.ajaxUrl, data )
                         .then( function ( response ) {
                             self.gettextRequestsLeft--
                         })
                         .catch( function ( error ) {
                             self.gettextRequestsLeft--
                             console.log( error )

                         } )

                })
            },
            getGettextStringsDictionaries(){
                let self = this

                let data = new FormData()
                data.append( 'action', 'etm_string_translation_get_strings_by_original_ids_gettext' )
                data.append( 'original_ids', JSON.stringify( self.gettextOriginalIds ) )
                data.append('language'     , this.currentLanguage)
                data.append( 'security', self.nonces['get_strings_by_original_id'] )
                axios.post( self.ajaxUrl, data )
                     .then( function ( response ) {
                         self.loadingStrings--
                         self.addToDictionary( response.data.dictionary, self.gettextNodeData )
                     })
                     .catch( function ( error ) {
                         self.loadingStrings--
                         self.dictionary = {}

                         console.log( error )
                         let reload = confirm( self.stEditorStrings.request_error )
                         if( reload === false ){
                             return
                         }else{
                             window.location.reload();
                         }

                     } )

            },
            alreadyDetected( selector, dbId ){
                let combined = selector + '=' + dbId
                if ( utils.arrayContainsItem( this.detectedSelectorAndId, combined ) ) {
                    return true
                }else {
                    this.detectedSelectorAndId.push(combined)
                    return false
                }
            },
            setupEventListener( node ){
                if ( node.tagName == 'A' && !node.hasAttribute( 'data-etmgettextoriginal' ) )
                    return false

                let self = this

                node.addEventListener( 'mouseenter', self.$refs.hoverActions.showPencilIcon )
            },
            addToDictionary( responseData, nodeInfo = null ){
                let self = this

                if ( responseData != null ) {
                    if ( nodeInfo ){
                        let responseIndexesFound = []
                        nodeInfo.forEach(function ( infoRow, index ){
                            responseData.some( function ( responseDataRow, responseIndex ) {

                                if ( infoRow.dbID == responseDataRow.dbID ) {
                                    //bring block_type to the top level object
                                    if ( responseDataRow.type != 'gettext' && typeof responseDataRow.block_type == 'undefined' ) {
                                        let firstLanguage = self.orderedSecondaryLanguages[0]

                                        if ( typeof responseDataRow.translationsArray[firstLanguage].block_type != 'undefined' )
                                            responseDataRow.block_type = responseDataRow.translationsArray[firstLanguage].block_type
                                    }

                                    nodeInfo[index] = Object.assign( {}, responseDataRow, infoRow )
                                    responseIndexesFound.push(responseIndex)
                                    return true // a sort of break
                                }
                            })
                        })
                        // console.log(responseIndexesFound)
                        let restOfResponseData = []
                        responseData.forEach(function ( row, index ){
                            if( !responseIndexesFound.includes(index)){
                                restOfResponseData.push(responseData[index]);
                            }
                        })
                        // console.log(responseData)
                        nodeInfo = nodeInfo.concat(restOfResponseData)
                    }else{
                        nodeInfo = responseData
                    }

                    this.stringGroups = this.addToStringGroups( nodeInfo )
                    this.dictionary = this.dictionary.concat( nodeInfo )

                    this.initStringsDropdown()
                }
            },
            addToStringGroups( strings ){

                // see what node groups are found
                let foundStringGroups = this.stringGroups;
                strings.forEach( function ( string ) {
                    if ( foundStringGroups.indexOf( string.group ) === -1 && ( ( typeof string.blockType === 'undefined' ) || string.blockType !== '2' ) ){
                        foundStringGroups.push( string.group )
                    }
                })

                // put the node groups in the order that we want, according to the prop this.stringGroupOrder
                let orderedStringGroups = [];

                if ( this.editorStrings.seo_update_notice != 'seo_pack_update_not_needed' ){
                    orderedStringGroups.push( this.editorStrings.seo_update_notice );
                }

                this.stringGroupOrder.forEach( function( group ){
                    if ( foundStringGroups.indexOf( group ) !== -1 ){
                        orderedStringGroups.push( group )
                    }
                })

                // if there were any other string groups that were not in the prop, add them at the end.
                foundStringGroups.forEach( function (group) {
                    if ( orderedStringGroups.indexOf( group ) === -1 ){
                        orderedStringGroups.push(group);
                    }
                })

                return orderedStringGroups;
            },
            getStringIndex( selector, dbID ){
                let found = null

                this.dictionary.some(function ( string, index ) {
                    if ( string.dbID == dbID && string.selector == selector ){
                        found = index
                        return true
                    }
                })

                return found
            },
            getNodeInfo( node, baseSelector = '' ){
                let stringId
                let nodeData  = []
                let selectors = this.prepareSelectorStrings( baseSelector )

                selectors.forEach( function ( selector ) {

                    stringId = node.getAttribute( selector )

                    if ( stringId ) {

                        let nodeAttribute   = selector.replace( baseSelector, '' )
                        let nodeGroup       = node.getAttribute( 'data-etm-node-group' + nodeAttribute )
                        let nodeDescription = node.getAttribute( 'data-etm-node-description' + nodeAttribute )

                        let entry = {
                            dbID      : stringId,
                            selector  : selector,
                            attribute : nodeAttribute.substr(1), // substr(1) is used to trim prefixing line - ex. -alt will result in alt (no line)
                        }

                        if ( nodeGroup )
                            entry.group = nodeGroup

                        if ( nodeDescription )
                            entry.description = nodeDescription

                        nodeData.push( entry )
                    }

                })

                return nodeData
            },
            getAllSelectors(){
                let selectors = []
                let self      = this

                this.dataAttributes.forEach( function ( dataAttribute ){
                    selectors = selectors.concat( self.prepareSelectorStrings( dataAttribute ) )
                })

                return selectors
            },
            prepareSelectorStrings( baseNameSelector ){
                let parsed_selectors = []

                this.selectors.forEach( function ( selectorSuffix, index ){
                    parsed_selectors.push( baseNameSelector + selectorSuffix  )
                })

                return parsed_selectors
            },
            parentURL( url ){
                return url.replace( 'etm-edit-translation=preview', 'etm-edit-translation=true' )
            },
            cleanURL( url ){
                //make removeUrlParameter recursive and only call it once with all the parameters that
                //need to stripped ?
                url = utils.removeUrlParameter( url, 'lang' )
                url = utils.removeUrlParameter( url, 'etm-view-as' )
                url = utils.removeUrlParameter( url, 'etm-view-as-nonce' )
                url = utils.removeUrlParameter( url, 'etm-edit-translation' )

                return url
            },
            showString( string, type ){
                if ( typeof string.blockType !== 'undefined' && string.blockType === '2' ){
                    // don't show deprecated translation blocks in the dropdown
                    return false
                }
                if ( type === this.editorStrings.images && typeof string.attribute != 'undefined' && string.attribute == 'src' )
                    return true

                if ( typeof string.attribute !== 'undefined' && ( string.attribute == 'href' || string.attribute == 'src' ) )
                    return false

                if ( string.group === type )
                    return true

                return false
            },
            initStringsDropdown(){
                let self = this

                if ( !this.isStringsDropdownOpen() ) {
                    jQuery( '#etm-string-categories' ).select2( 'destroy' )

                    jQuery( '#etm-string-categories' ).select2( { placeholder : self.editorStrings.select_string, templateResult: function(option){
                        let original    = he.decode( option.text.substring(0, 90) ) + ( ( option.text.length <= 90) ? '' : '...' )
                        let description = ( option.title ) ?  '(' + option.title + ')' : ''

                        return jQuery( '<div>' + original + '</div><div class="string-selector-description">' + description + '</div>' );
                    }, width : '100%' } ).prop( 'disabled', false )

                    jQuery( '#etm_select2_overlay' ).hide()
                }
            },
            stringsDropdownLoading(){
                jQuery( '#etm-string-categories' ).select2( { placeholder : this.editorStrings.strings_loading, width : '100%' } ).prop( 'disabled', true )
            },
            processOptionName( name, type ){
                if ( type == 'Images' || ( utils.isURL( name ) && type == 'Meta Information' ) )
                    return utils.getFilename( name )

                return utils.escapeHtml( name )
            },
            isStringsDropdownOpen(){
                return jQuery( '#etm-string-categories' ).select2( 'isOpen' )
            },
            hasUnsavedChanges(){
                let unsavedChanges = false
                let self = this
                if ( this.selectedIndexesArray.length > 0 ) {
                    this.selectedIndexesArray.forEach(function (selectedIndex) {
                        self.settings['translation-languages'].forEach(function (languageCode) {
                            if (self.dictionary[selectedIndex] &&
                                self.dictionary[selectedIndex].translationsArray[languageCode] &&
                                (self.dictionary[selectedIndex].translationsArray[languageCode].translated !== self.dictionary[selectedIndex].translationsArray[languageCode].editedTranslation)) {
                                unsavedChanges = true
                            }
                        })
                    })
                }
                this.showChangesUnsavedMessage = unsavedChanges

                return unsavedChanges
            },
            iframeLoader( status ) {
                let loader = document.getElementById( 'etm-preview-loader' )

                if( status == 'show' )
                    loader.style.display = 'flex'
                else if( status == 'hide' )
                    loader.style.display = 'none'
            },
            previousString(){
                let currentValue = document.getElementById('etm-string-categories').value

                let newValue = +currentValue - 1

                while( newValue >= 0 && document.querySelectorAll('#etm-string-categories option[value="' + newValue + '"]').length === 0 ){
                    newValue--;
                }

                if( newValue < 0 )
                    return

                this.selectedString = newValue.toString()
            },
            nextString(){
                let currentValue = document.getElementById('etm-string-categories').value, newValue = 0

                if( currentValue != '' )
                    newValue = +currentValue + 1

                while( newValue < this.dictionary.length && document.querySelectorAll('#etm-string-categories option[value="' + newValue + '"]').length === 0 ){
                    newValue++;
                }

                if ( newValue >= this.dictionary.length ){
                    return
                }

                this.selectedString = newValue.toString()
            },
            addKeyboardShortcutsListener(){
                document.addEventListener("keydown", function(e) {
                    if ((window.navigator.platform.match("Mac") ? e.metaKey : e.ctrlKey) && e.altKey ) {
                        // CTRL + ALT + right arrow
                        if( e.keyCode === 39 ){
                            e.preventDefault();
                            window.dispatchEvent( new Event( 'etm_trigger_next_string_event' ) );
                        }else{
                            // CTRL + ALT + left arrow
                            if( e.keyCode === 37 ) {
                                e.preventDefault();
                                window.dispatchEvent( new Event( 'etm_trigger_previous_string_event' ) );
                            }
                        }
                    }
                }, false);

                window.addEventListener( 'etm_trigger_next_string_event', this.nextString )
                window.addEventListener( 'etm_trigger_previous_string_event', this.previousString )
            },
            addHelpPanelListeners(){
                let self = this
                window.addEventListener( 'etm_switch_language_help_panel', function(){
                    self.highlightLanguageSwitcher = true
                } )
                window.addEventListener( 'etm_search_string_help_panel', function(){
                    self.highlightStringList = true
                } )
                window.addEventListener( 'etm_help_panel_changed', function(){
                    self.highlightLanguageSwitcher = false
                    self.highlightStringList = false
                } )

                document.addEventListener( 'etm_update_user_meta',  this.updateUserMeta )
            },
            resizeIframe (event, ui) {
                let total_width = jQuery(window).width();
                let width = jQuery( '#etm-controls' ).width();

                let previewContainer = jQuery( '#etm-preview' );
                previewContainer.css('right', width );
                previewContainer.css('left', ( width - 348 ) );
                previewContainer.css('width', (total_width - width));
            },
            updateUserMeta( data ){
                let key = data.detail.userMetaKey
                let value = data.detail.userMetaValue
                this.userMeta[key] = value

                let formData = new FormData()
                formData.append( 'action', 'etm_save_editor_user_meta' )
                formData.append( 'security', this.nonces[ 'etm_editor_user_meta' ] )
                formData.append( 'user_meta', JSON.stringify({[key] : value } ) )
                axios.post(this.ajaxUrl, formData)
                     .catch(function (error) {
                         console.log(error);
                     });
            }

        },
        //add support for v-model in select2
        directives: {
            select2: {
                inserted(el) {
                    jQuery(el).on('select2:select', () => {
                        const event = new Event('change', { bubbles: true, cancelable: true })
                        el.dispatchEvent(event)
                    })

                    jQuery(el).on('select2:unselect', () => {
                        const event = new Event('change', { bubbles: true, cancelable: true })
                        el.dispatchEvent(event)
                    })
                },
            }
        }
    }
</script>
